import { env } from "@acme/env/client";
import type { Messaging } from "firebase/messaging";
import { getToken, onMessage } from "firebase/messaging";
import { getFirebaseMessagingServiceWorkerRegistration } from "./service-worker.js";

const VAPID_KEY = env.PUBLIC_FIREBASE_VAPID_KEY;

export interface NotificationPayload {
  title?: string;
  body?: string;
  image?: string;
  tag?: string;
}

/**
 * Request notification permission from the user
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!("Notification" in window)) {
    console.log("This browser does not support notifications");
    return "denied";
  }

  const permission = await Notification.requestPermission();
  return permission;
};

/**
 * Get FCM token for the device
 * Note: Assumes permission is already granted - call requestNotificationPermission() first
 */
export const getFCMToken = async (messaging: Messaging | null): Promise<string | null> => {
  if (!messaging) {
    console.log("Messaging not initialized");
    return null;
  }

  if (Notification.permission !== "granted") {
    console.log("Notification permission not granted - cannot get token");
    return null;
  }

  try {
    const registration = await getFirebaseMessagingServiceWorkerRegistration();

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      console.log("FCM Token:", token);
      return token;
    } else {
      console.log("No registration token available");
      return null;
    }
  } catch (err) {
    console.error("An error occurred while retrieving token:", err);
    return null;
  }
};

/**
 * Setup foreground message listener
 */
export const onForegroundMessage = (
  messaging: Messaging | null,
  callback: (payload: NotificationPayload, data?: Record<string, string | undefined>) => void
) => {
  if (!messaging) {
    console.log("Messaging not initialized");
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    console.log("Foreground message received:", payload);

    const data = payload.data as Record<string, string | undefined> | undefined;

    const notificationData: NotificationPayload = {
      title: payload.notification?.title ?? data?.title,
      body: payload.notification?.body ?? data?.body,
      image: payload.notification?.image ?? data?.icon,
      tag: data?.tag,
    };

    callback(notificationData, data);
  });
};

/**
 * Display a notification (for foreground messages)
 */
export const showNotification = async (payload: NotificationPayload) => {
  if (!("Notification" in window)) {
    console.log("Notifications not supported");
    return;
  }

  if (Notification.permission !== "granted") {
    console.log("Notification permission not granted");
    return;
  }

  const title = payload.title || "New Message";
  const options: NotificationOptions & { renotify?: boolean } = {
    body: payload.body,
    icon: payload.image || "/logo.png",
    badge: "/logo.png",
    tag: payload.tag ?? "fcm-notification",
    renotify: true,
  };

  try {
    if ("serviceWorker" in navigator) {
      const registration = await getFirebaseMessagingServiceWorkerRegistration();
      await registration.showNotification(title, options);
      console.log("✅ Notification displayed via Service Worker");
    } else {
      new Notification(title, options);
      console.log("✅ Notification displayed via Notification API");
    }
  } catch (err) {
    console.error("❌ Failed to show notification:", err);
  }
};
